import React, {useEffect, useState} from 'react';
import {
    Avatar,
    Badge,
    Box,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Divider,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Typography
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import dayjs from 'dayjs';
import {useSabitler} from '../../../../hooks/queries/useLogbookQueries';
import logbookApi from "../../../../api/staffApi/logbookApi";
import {IMesaiKayit} from "../../../../constants/types/logbook";
import {useSelector} from "../../../../redux/store";

const GunlukPersonelOzeti = () => {
    const {data: sabitler} = useSabitler();
    const userId = useSelector(state => state.auth.user_id);
    const [isLoading, setIsLoading] = useState(true);
    const [haftalikVeri, setHaftalikVeri] = useState<IMesaiKayit[]>([]);

    const bugunStr = dayjs().format("DD-MM-YYYY");
    const yediGunSonraStr = dayjs().add(6, 'day').format("DD-MM-YYYY");

    useEffect(() => {
        setIsLoading(true);
        logbookApi.getTakvimKayitlari({
            baslangicTarih: bugunStr,
            bitisTarih: yediGunSonraStr
        }).then((res) => {
            setHaftalikVeri(res.mesailer || []);
            setIsLoading(false);
        }).catch(() => {
            setIsLoading(false);
        });
    }, [bugunStr, yediGunSonraStr]);

    if (isLoading) {
        return (
            <Card sx={{mb: 2, display: 'flex', justifyContent: 'center', p: 3}}>
                <CircularProgress size={24}/>
            </Card>
        );
    }

    if (!sabitler) return null;

    // Bugün çalışanlar (Tüm personel)
    const bugunCalisanlar = haftalikVeri
        .filter(m => m.tarih === bugunStr)
        .sort((a, b) => (a.baslangic_saat || '').localeCompare(b.baslangic_saat || ''));

    // Benim haftalık şiftlerim
    const benimSiftlerim = haftalikVeri
        .filter(m => m.kisi === userId)
        .sort((a, b) => {
            const dateA = dayjs(a.tarih, "DD-MM-YYYY");
            const dateB = dayjs(b.tarih, "DD-MM-YYYY");
            if (dateA.isSame(dateB)) {
                return (a.baslangic_saat || '').localeCompare(b.baslangic_saat || '');
            }
            return dateA.isBefore(dateB) ? -1 : 1;
        });

    return (
        <Box>
            {/* BUGÜN ÇALIŞANLAR */}
            <Card sx={{mb: 2}}>
                <CardHeader
                    title="Bugün Çalışanlar"
                    subheader={dayjs().format('DD MMMM YYYY, dddd')}
                    avatar={<Badge badgeContent={bugunCalisanlar.length} color="primary"><PersonIcon
                        color="action"/></Badge>}
                />
                <Divider/>
                <CardContent sx={{p: 0}}>
                    {bugunCalisanlar.length > 0 ? (
                        <List>
                            {bugunCalisanlar.map((mesai, index) => {
                                const kisi = sabitler.kisiler.find((k: any) => k.id === mesai.kisi);
                                return (
                                    <ListItem key={index} sx={{py: 0.5}}>
                                        <ListItemAvatar>
                                            <Avatar sx={{
                                                bgcolor: kisi?.renk || '#757575',
                                                width: 32,
                                                height: 32,
                                                fontSize: '0.875rem'
                                            }}>
                                                {kisi?.rumuz ? kisi.rumuz[0] : '?'}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={<Typography variant="body2"
                                                                 fontWeight={mesai.kisi === userId ? "bold" : "normal"}>
                                                {kisi?.rumuz || 'Bilinmeyen'}
                                            </Typography>}
                                            secondary={
                                                <Box component="span"
                                                     sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                                                    <Typography component="span" variant="caption"
                                                                color="textSecondary">
                                                        {mesai.baslangic_saat?.slice(0, 5)}
                                                    </Typography>
                                                    {mesai.ekstra && <Chip label="Ekstra" size="small" color="primary"
                                                                           variant="outlined"
                                                                           sx={{height: 16, fontSize: '0.65rem'}}/>}
                                                </Box>
                                            }
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    ) : (
                        <Box sx={{p: 2, textAlign: 'center'}}>
                            <Typography variant="body2" color="textSecondary">Bugün için mesai kaydı
                                bulunamadı.</Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>

            {/* HAFTALIK ŞİFTLERİM */}
            <Card sx={{mb: 2, borderLeft: '4px solid #2196f3'}}>
                <CardHeader
                    titleTypographyProps={{variant: 'subtitle1', fontWeight: 'bold'}}
                    title="Haftalık Şiftlerim"
                    avatar={<CalendarMonthIcon color="primary" fontSize="small"/>}
                    sx={{pb: 1}}
                />
                <Divider/>
                <CardContent sx={{p: 0}}>
                    {benimSiftlerim.length > 0 ? (
                        <List>
                            {benimSiftlerim.map((mesai, index) => {
                                const tarihObj = dayjs(mesai.tarih, "DD-MM-YYYY");
                                const isBugun = tarihObj.format("DD-MM-YYYY") === bugunStr;
                                return (
                                    <React.Fragment key={index}>
                                        <ListItem
                                            sx={{py: 1, bgcolor: isBugun ? 'rgba(33, 150, 243, 0.04)' : 'transparent'}}>
                                            <ListItemText
                                                primary={
                                                    <Box sx={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center'
                                                    }}>
                                                        <Typography variant="body2"
                                                                    fontWeight={isBugun ? "bold" : "medium"}>
                                                            {isBugun ? "Bugün" : tarihObj.format('DD MMM, dddd')}
                                                        </Typography>
                                                        <Typography variant="body2" color="primary" fontWeight="bold">
                                                            {mesai.baslangic_saat?.slice(0, 5)}
                                                        </Typography>
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box sx={{display: 'flex', gap: 1, mt: 0.5}}>
                                                        {mesai.tam_gun && <Chip label="Tam Gün" size="small"
                                                                                sx={{height: 18, fontSize: '0.7rem'}}/>}
                                                        {mesai.ekstra &&
                                                            <Chip label="Ekstra" size="small" color="secondary"
                                                                  sx={{height: 18, fontSize: '0.7rem'}}/>}
                                                        {!mesai.tam_gun && !mesai.ekstra && mesai.saat > 0 &&
                                                            <Chip label={`${mesai.saat} Saat`} size="small"
                                                                  variant="outlined"
                                                                  sx={{height: 18, fontSize: '0.7rem'}}/>}
                                                    </Box>
                                                }
                                            />
                                        </ListItem>
                                        {index < benimSiftlerim.length - 1 && <Divider component="li"/>}
                                    </React.Fragment>
                                );
                            })}
                        </List>
                    ) : (
                        <Box sx={{p: 2, textAlign: 'center'}}>
                            <Typography variant="body2" color="textSecondary">Önümüzdeki 7 gün için şiftiniz
                                bulunmuyor.</Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default GunlukPersonelOzeti;
